/* Owns permission gating in the route tree (PANEL.md §3, §4). `can()` from
   @sentinelac/shared is the single source of truth — the API's `requireRole`
   preHandler calls the same function, so the panel and the server can never
   disagree about who may do what.

   Two behaviours, both required by the spec: route level renders
   `PermissionDenied`, control level hides (pass `fallback={null}`) — never a
   silently broken page. */

import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, Outlet, useParams } from 'react-router'

import { Badge } from '../components/ui/Badge.js'
import { useT } from '../i18n/index.js'
import { useAuthState } from '../lib/auth-store.js'
import { roleLabelKey } from '../lib/format.js'
import { canRole, useRole, type Permission, type Role } from '../lib/permissions.js'

interface DeniedPanelProps {
  /** The requirement that failed, shown in mono so it can be pasted to an admin verbatim. */
  requirement: string
  role: Role | null
  backTo: string
}

function DeniedPanel({ requirement, role, backTo }: DeniedPanelProps): ReactNode {
  const t = useT()
  return (
    <section
      role="alert"
      className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-16 text-center"
      data-testid="permission-denied"
    >
      <Lock aria-hidden className="size-8 text-text-faint" />
      <h1 className="text-lg font-medium text-text">{t('common.permissionDenied')}</h1>
      <p className="font-mono text-xs text-text-faint select-all">{requirement}</p>
      {role ? (
        <Badge tone="neutral" variant="outline">
          {t(roleLabelKey(role))}
        </Badge>
      ) : null}
      <Link
        to={backTo}
        className="rounded-md border border-border-strong px-3 py-2 text-sm text-text hover:bg-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {t('common.goHome')}
      </Link>
    </section>
  )
}

export interface PermissionDeniedProps {
  permission: Permission
  role: Role | null
  backTo?: string
}

/**
 * The route-level denial of PANEL.md §3. It names the permission and the
 * caller's own role, because "you cannot see this" without "you are a viewer"
 * sends people to their admin with nothing useful to say.
 */
export function PermissionDenied({ permission, role, backTo = '/servers' }: PermissionDeniedProps): ReactNode {
  return <DeniedPanel requirement={permission} role={role} backTo={backTo} />
}

export interface RequireRoleProps {
  permission: Permission
  /** Defaults to the active server; pass explicitly when gating another server's data. */
  serverId?: string | null
  /** Omit to gate a route (`<Outlet/>` is rendered when allowed). */
  children?: ReactNode
  /** `undefined` renders `PermissionDenied`; `null` hides the subtree entirely. */
  fallback?: ReactNode
}

export function RequireRole({ permission, serverId, children, fallback }: RequireRoleProps): ReactNode {
  const params = useParams()
  // A route gate sits under `/s/:serverId`, so the param is the authoritative id
  // even before `ServerLayout` has published the active server to the store.
  const resolved = serverId === undefined ? (params['serverId'] ?? null) : serverId
  const role = useRole(resolved)
  const allowed = canRole(role, permission)

  if (allowed) return children === undefined ? <Outlet /> : <>{children}</>
  if (fallback !== undefined) return <>{fallback}</>

  const backTo = resolved ? `/s/${resolved}/dashboard` : '/servers'
  return <PermissionDenied permission={permission} role={role} backTo={backTo} />
}

/**
 * `/admin/global-bans` is gated by `UserDto.isPlatformAdmin`, which is not a
 * per-server permission and therefore not expressible through `can()`.
 */
export function RequirePlatformAdmin({ children }: { children?: ReactNode }): ReactNode {
  const { user } = useAuthState()
  if (user?.isPlatformAdmin === true) return children === undefined ? <Outlet /> : <>{children}</>
  return <DeniedPanel requirement="isPlatformAdmin" role={null} backTo="/servers" />
}
